import { z } from 'zod';
import { OnlineStatus } from './auth.js';
export const CreateAgentInput = z.object({
    email: z.string().email().toLowerCase(),
    password: z.string().min(8).max(128),
    name: z.string().min(1).max(100),
});
/**
 * Owner-controlled mutations on an agent row.
 *
 * NOTE: `password` is intentionally NOT settable here. Agents recover
 * their own password through the email-based /v1/auth/forgot-password
 * flow, which keeps the owner out of the password-rotation path and
 * means the owner can't silently rotate an agent's credentials. The
 * initial password supplied at agent creation (`CreateAgentInput`) is
 * still owner-set, since the agent doesn't have an account yet.
 */
export const UpdateAgentInput = z.object({
    name: z.string().min(1).max(100).optional(),
    suspended: z.boolean().optional(),
});
export const SetPresenceInput = z.object({
    status: OnlineStatus,
});
export const AgentDTO = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string().nullable(),
    onlineStatus: OnlineStatus,
    suspended: z.boolean(),
    activeConversations: z.number().int().nonnegative(),
    lastSeenAt: z.string().nullable(),
    createdAt: z.string(),
});
export const TeamSummary = z.object({
    agents: z.array(AgentDTO),
    maxAgents: z.number().int().nonnegative(),
    usedAgents: z.number().int().nonnegative(),
});
export const WsTicketResponse = z.object({
    ticket: z.string().min(1),
    expiresAt: z.string(),
});
// ----- Per-org handoff settings -----
export const HandoffFallbackStrategy = z.enum(['offline_message', 'auto_assign']);
export const HandoffSettingsDTO = z.object({
    /** How long the offer stays Take-able before the sweeper runs the
     *  fallback. Bounded so a misconfigured value can't strand visitors. */
    claimWindowSeconds: z.number().int().min(5).max(600),
    fallbackStrategy: HandoffFallbackStrategy,
    /** Master switch for Telegram fanout of new pending handoffs. */
    notifyOnPending: z.boolean(),
    updatedAt: z.string(),
});
export const UpdateHandoffSettingsInput = z.object({
    claimWindowSeconds: z.number().int().min(5).max(600).optional(),
    fallbackStrategy: HandoffFallbackStrategy.optional(),
    notifyOnPending: z.boolean().optional(),
});
// ----- Telegram bot configuration -----
/** What an owner sees about their org's bot. Token is NEVER returned. */
export const OwnerTelegramBotDTO = z.object({
    botUsername: z.string().min(1).max(64),
    botId: z.number().int(),
    webhookUrl: z.string().url(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const RegisterTelegramBotInput = z.object({
    /** BotFather token, format `<numeric>:<35-character secret>`. We don't
     *  enforce the structure beyond the basic shape; getMe is the real
     *  validator. */
    token: z.string().min(40).max(128).regex(/^[0-9]+:[A-Za-z0-9_-]{20,}$/),
});
/** What a team member sees about their own Telegram link. */
export const AgentTelegramLinkDTO = z.object({
    tgUserId: z.number().int(),
    tgUsername: z.string().nullable(),
    linkedAt: z.string(),
});
/** Composite "what does my team's Telegram look like" response. */
export const TelegramSummaryDTO = z.object({
    bot: OwnerTelegramBotDTO.nullable(),
    myLink: AgentTelegramLinkDTO.nullable(),
});
export const TelegramLinkCodeResponse = z.object({
    /** Full deep link the agent should open. */
    url: z.string().url(),
    /** Same code embedded in the URL, surfaced separately so the dashboard
     *  can show it as a fallback ("If the link doesn't open, paste this in
     *  the bot chat: /start <code>"). */
    code: z.string().min(1).max(64),
    /** Wall-clock expiry. */
    expiresAt: z.string(),
});
//# sourceMappingURL=team.js.map